import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import SplashCursor from './components/SplashCursor';
import VideoDownloader from './components/VideoDownloader';
import Footer from './components/Footer';
import Icon from '../../components/AppIcon';


const YouTubeVideoDownloaderLandingPage = () => {
  useEffect(() => {
    // Scroll to top on component mount
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Zee Downloader - YouTube Video Downloader | Fast & Secure</title>
        <meta 
          name="description" 
          content="Download YouTube videos in high quality with Zee Downloader. Fast, secure, and easy-to-use video downloader with glassmorphism design. Support for 4K, 1080p, 720p formats." 
        />
        <meta name="keywords" content="youtube downloader, video downloader, download youtube videos, HD video download, 4K video download" />
        <meta name="author" content="Zee Downloader" />
        <meta property="og:title" content="Zee Downloader - YouTube Video Downloader" />
        <meta property="og:description" content="Download YouTube videos in high quality with our fast and secure downloader." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="/you-tube-video-downloader-landing-page" />
      </Helmet>
      <div className="min-h-screen bg-background relative overflow-hidden">
        {/* Interactive Background */}
        <SplashCursor />
        
        {/* Semi-transparent Overlay - Updated for black theme */}
        <div className="fixed inset-0 bg-background/30 backdrop-blur-sm" style={{ zIndex: 2 }} />
        
        {/* Header */}
        <div className="relative" style={{ zIndex: 50 }}>
          <Header />
        </div>

        {/* Main Content */}
        <main className="relative pt-20 pb-8" style={{ zIndex: 10 }}>
          <div className="glass-container">
            {/* Hero Section */}
            <div className="text-center py-12 mb-8">
              <div className="glass-card p-8 mb-8 max-w-4xl mx-auto">
                <h1 className="text-fluid-4xl font-bold text-foreground mb-4 leading-tight">
                  Download YouTube Videos
                  <span className="block text-accent">In High Quality</span>
                </h1>
                <p className="text-fluid-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                  Experience the fastest and most reliable YouTube video downloader with our beautiful glassmorphism interface. 
                  Download videos in 4K, 1080p, 720p, and more formats with just a few clicks.
                </p>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto mb-12">
                {[
                  { number: '100K+', label: 'Happy Users', icon: 'Users' },
                  { number: '1M+', label: 'Videos Downloaded', icon: 'Download' },
                  { number: '99.9%', label: 'Uptime', icon: 'Zap' }
                ]?.map((stat, index) => (
                  <div key={index} className="glass-card p-6 text-center micro-lift glass-hover">
                    <div className="glass-card p-3 w-fit mx-auto mb-3">
                      <Icon name={stat?.icon} size={24} className="text-accent" />
                    </div>
                    <div className="text-fluid-2xl font-bold text-foreground mb-1">
                      {stat?.number}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {stat?.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Video Downloader Component */}
            <VideoDownloader />

            {/* How It Works Section */}
            <div className="mt-20 mb-16">
              <div className="text-center mb-12">
                <h2 className="text-fluid-3xl font-bold text-foreground mb-4">
                  How It Works
                </h2>
                <p className="text-fluid-base text-muted-foreground max-w-2xl mx-auto">
                  Download your favorite YouTube videos in just three simple steps
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  {
                    step: '01',
                    title: 'Paste URL',
                    description: 'Copy and paste the YouTube video URL into the input field',
                    icon: 'Link'
                  },
                  {
                    step: '02',
                    title: 'Choose formats',
                    description: 'Select your preferred formats quality of video or audio',
                    icon: 'Settings'
                  },
                  {
                    step: '03',
                    title: 'Download',
                    description: 'Click download and get your video file in seconds',
                    icon: 'Download'
                  }
                ]?.map((item, index) => (
                  <div key={index} className="relative">
                    <div className="glass-card p-8 text-center micro-lift glass-hover h-full">
                      <div className="glass-card p-4 w-fit mx-auto mb-6">
                        <Icon name={item?.icon} size={32} className="text-accent" />
                      </div>
                      <div className="text-sm font-mono text-accent mb-2">
                        STEP {item?.step}
                      </div>
                      <h3 className="text-fluid-xl font-semibold text-foreground mb-4">
                        {item?.title}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {item?.description}
                      </p>
                    </div>
                    
                    {/* Connection Line */}
                    {index < 2 && (
                      <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-accent to-transparent transform -translate-y-1/2" />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ Section */}
            <div className="mt-20 mb-16">
              <div className="text-center mb-12">
                <h2 className="text-fluid-3xl font-bold text-foreground mb-4">
                  Frequently Asked Questions
                </h2>
                <p className="text-fluid-base text-muted-foreground max-w-2xl mx-auto">
                  Everything you need to know about Zee Downloader
                </p>
              </div>

              <div className="max-w-3xl mx-auto space-y-4">
                {[
                  {
                    question: 'Is Zee Downloader free to use?',
                    answer: 'Yes, Zee Downloader is completely free to use. You can download unlimited YouTube videos without any cost or registration required.'
                  },
                  {
                    question: 'What video qualities are supported?',
                    answer: 'We support all major video qualities including 4K (2160p), 1080p HD, 720p HD, 480p SD, and 360p. The available qualities depend on the original video upload.'
                  },
                  {
                    question: 'Is it safe to use?',
                    answer: 'Absolutely! Zee Downloader uses SSL encryption to protect your privacy and data. We do not store any personal information or downloaded content on our servers.'
                  },
                  {
                    question: 'Can I download videos on mobile devices?',
                    answer: 'Yes, Zee Downloader is fully responsive and works perfectly on all devices including smartphones, tablets, and desktop computers.'
                  }
                ]?.map((faq, index) => (
                  <div key={index} className="glass-card p-6">
                    <h3 className="text-fluid-lg font-semibold text-foreground mb-3">
                      {faq?.question}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {faq?.answer}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <div className="relative" style={{ zIndex: 10 }}>
          <Footer />
        </div>
      </div>
    </>
  );
};

export default YouTubeVideoDownloaderLandingPage;